import { AgGridTable } from 'components/AgGrid/AgGridTable';
import { ConfirmationDialog } from 'components/ConfirmationDialog';
import { TableSettings } from 'components/TableSettings';
import { TotalCount } from 'components/TotalCount';
import { Grid, GridItem, Text } from 'uikit';

import type { ColDef, ColGroupDef, RowClassRules, SortChangedEvent } from 'ag-grid-community';
import type { AgGridReact } from 'ag-grid-react';
import { useRef } from 'react';
import type { Control } from 'react-hook-form';

import { WorkOrderActsKc2ListGroupManager } from '../WorkOrderActsKc2ListGroupManager';

import Styles from './WorkOrderActsKC2List.module.css';

type TWorkOrderActsKc2ListProps<TMain, TGroup = any> = {
    groupManagerControl: Control<any>;
    actKc2List: Partial<TMain>[];
    tableColumns: (ColDef<Partial<TMain>> | ColGroupDef<Partial<TMain>>)[];
    groupManagerData: TGroup[];
    groupManagerColumns: (ColDef<TGroup> | ColGroupDef<TGroup>)[];
    isDeleteOpened?: boolean;
    confirmDeletion: () => void;
    cancelDeletion: () => void;
    handleToggleModalState: () => void;
    handleSaveGroup: () => void;
    defaultColDef?: ColDef<any>;
    isLoading: boolean;
    groupManagerOpened: boolean;
    isCommit: boolean;
    isConfirmDisabled: boolean;
    isFormDirty: boolean;
    noRowsTemplate: string;
    groupManagerAvailable: boolean;
    fetchedDataCount: number;
    totalItemsCount: number;
    changeDataOrderHandler?: (params: SortChangedEvent<Partial<TMain>>) => void;
    tableSettingsColumns?: any[];
    handleTableSettingsConfirm?: (s: any) => void;
    isColumnVisible?: (t: string) => boolean;
    isEdit?: boolean;
    tableContext: {
        tableUniqueId: string;
    };
};

const rowClassGetter: RowClassRules = {
    isGroupRow: ({ data }) => !!data?.isGroup,
    isCommonRow: ({ data }) => !data?.isGroup,
};

export const WorkOrderActsKc2ListTable = <
    TMainTable extends { isGroup: boolean; groupType: TGroupEnumType },
    TGroupManagerData = any,
    TGroupEnumType = any,
>(
    props: TWorkOrderActsKc2ListProps<TMainTable, TGroupManagerData>,
) => {
    const gridRef = useRef<AgGridReact>(null);

    const {
        groupManagerAvailable,
        totalItemsCount,
        fetchedDataCount,
        groupManagerColumns,
        groupManagerData,
        groupManagerOpened,
        handleToggleModalState,
        groupManagerControl,
        isLoading,
        isCommit,
        isConfirmDisabled,
        isFormDirty,
        handleSaveGroup,
        isDeleteOpened,
        confirmDeletion,
        cancelDeletion,
        defaultColDef,
        tableColumns,
        actKc2List,
        noRowsTemplate = 'Нет данных',
        changeDataOrderHandler,
        tableSettingsColumns,
        handleTableSettingsConfirm,
        isColumnVisible,
        isEdit,
        tableContext,
    } = props;

    return (
        <div className={`ag-theme-alpine ${Styles.WorkOrderActsKC2List} agCustomHeaderColors`}>
            <Grid cols={2} className={Styles.tableSettings}>
                <GridItem className={Styles.tableSettingsLeftContent}>
                    {!!fetchedDataCount && <TotalCount totalCount={fetchedDataCount} />}
                    &nbsp;
                    {!!fetchedDataCount && (
                        <Text view="secondary" size="s">
                            из {totalItemsCount}
                        </Text>
                    )}
                </GridItem>
                <GridItem className={Styles.tableSettingsRightContent}>
                    {groupManagerAvailable && (
                        <WorkOrderActsKc2ListGroupManager
                            isOpened={groupManagerOpened}
                            control={groupManagerControl}
                            handleSubmit={handleSaveGroup}
                            tableData={groupManagerData}
                            tableColumns={groupManagerColumns}
                            defaultColDef={defaultColDef}
                            btnClassName={Styles.btnGroupManager}
                            handleToggleModalState={handleToggleModalState}
                            isLoading={isLoading}
                            isCommit={isCommit}
                            btnConfirmDisabled={isConfirmDisabled}
                            isFormDirty={isFormDirty}
                            isEdit={isEdit}
                        />
                    )}

                    {!!(tableSettingsColumns && handleTableSettingsConfirm && isColumnVisible) && (
                        <TableSettings
                            columns={tableSettingsColumns}
                            onConfirm={handleTableSettingsConfirm}
                            isColumnVisible={isColumnVisible}
                            showPinColumn
                            tableApi={gridRef?.current}
                        />
                    )}
                </GridItem>
            </Grid>
            <AgGridTable
                ref={gridRef}
                columnDefs={tableColumns}
                rowData={actKc2List}
                rowClassRules={rowClassGetter}
                overlayNoRowsTemplate={noRowsTemplate}
                onSortChanged={changeDataOrderHandler}
                context={tableContext}
            />
            {isDeleteOpened && (
                <ConfirmationDialog
                    textHeader="Удаление группы актов"
                    textConfirmation="Вы уверены, что хотите удалить группу?"
                    cancelButtonText="Не удалять"
                    confirmButtonText="Удалить"
                    isOpened={isDeleteOpened}
                    onSubmit={confirmDeletion}
                    onClose={cancelDeletion}
                />
            )}
        </div>
    );
};

WorkOrderActsKc2ListTable.displayName = 'WorkOrderActsKc2ListTable';
